import { useEffect, useMemo, useRef, useState } from "react";
import AddressAutocomplete from "../components/AddressAutocomplete";
import { useAuth } from "../components/AuthGate";
import MessageThread from "../components/MessageThread";
import { useUnreadCount } from "../lib/useUnreadCount";
import { supabase } from "../lib/supabase";
import PropertyDetail from "./PropertyDetail";
import AskCisero from "./AskCisero";
import AgentGateModal from "./AgentGate";

const BETA_SECRET = process.env.REACT_APP_BETA_SECRET || "sixzero2026";
const BETA_KEY = "od_beta_token";
const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN || "";

// Demo mode — activated by ?demo=1 in URL. Uses pre-seeded listings so the
// product demo shows realistic data without real API calls or auth complexity.
const _demoParams = new URLSearchParams(window.location.search);
const IS_DEMO       = _demoParams.get("demo") === "1";
const DEMO_AUTOSEL  = IS_DEMO && _demoParams.get("autoselect") === "1";
const DEMO_AUTOOFFER = IS_DEMO && _demoParams.get("autooffer") === "1";

const DEMO_LISTINGS = IS_DEMO ? [
  { id: "demo-b1", address: "412 Oak Street",    city: "San Francisco", state: "CA", zip: "94114", price: 1195000, beds: 3, baths: 2, sqft: 1640, year_built: 1962, type: "single_family", lat: 37.7722, lng: -122.4295, photo: "https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=640&q=80&auto=format&fit=crop" },
  { id: "demo-b2", address: "88 Sunset Blvd",    city: "San Francisco", state: "CA", zip: "94122", price: 1349000, beds: 4, baths: 3, sqft: 2100, year_built: 1998, type: "single_family", lat: 37.7596, lng: -122.4785, photo: "https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=640&q=80&auto=format&fit=crop" },
  { id: "demo-b3", address: "33 Marina Way",     city: "San Francisco", state: "CA", zip: "94123", price: 995000,  beds: 2, baths: 2, sqft: 1280, year_built: 2005, type: "condo",         lat: 37.8003, lng: -122.4386, photo: "https://images.unsplash.com/photo-1512917774080-9991f1c4c750?w=640&q=80&auto=format&fit=crop" },
  { id: "demo-b4", address: "201 Valencia St",   city: "San Francisco", state: "CA", zip: "94103", price: 1085000, beds: 3, baths: 2, sqft: 1420, year_built: 1925, type: "single_family", lat: 37.7665, lng: -122.4217, photo: "https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=640&q=80&auto=format&fit=crop" },
  { id: "demo-b5", address: "777 Brannan Street", city: "San Francisco", state: "CA", zip: "94107", price: 1525000, beds: 3, baths: 3, sqft: 1890, year_built: 2018, type: "condo",         lat: 37.7710, lng: -122.4028, photo: "https://images.unsplash.com/photo-1523217582562-09d0def993a6?w=640&q=80&auto=format&fit=crop" },
] : [];

const fmtPrice = (n) => n == null ? "—" : `$${Math.round(n).toLocaleString()}`;
const fmtPriceShort = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 2).replace(/\.?0+$/, "")}M`;
  if (n >= 1_000)     return `$${Math.round(n / 1_000)}k`;
  return `$${n}`;
};

// ─── Mapbox loader (CDN, no new npm dep) ─────────────────────────────────────
const MAPBOX_VERSION = "3.7.0";
function loadMapbox() {
  if (window.mapboxgl) return Promise.resolve(window.mapboxgl);
  return new Promise((resolve, reject) => {
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.css`;
    document.head.appendChild(css);
    const s = document.createElement("script");
    s.src = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}/mapbox-gl.js`;
    s.onload = () => resolve(window.mapboxgl);
    s.onerror = () => reject(new Error("Failed to load Mapbox GL"));
    document.head.appendChild(s);
  });
}

// ─── CSS ─────────────────────────────────────────────────────────────────────
const CSS = `
  *{box-sizing:border-box}
  body{margin:0;font-family:'Inter','Outfit',sans-serif;background:#f2f8f5;color:#1c2b25}
  .b-app{height:100vh;display:flex;flex-direction:column;overflow:hidden}
  .b-top{display:flex;align-items:center;gap:14px;padding:10px 18px;border-bottom:1px solid rgba(45,122,98,0.15);background:#fff;flex-shrink:0;flex-wrap:wrap}
  .b-brand{display:flex;align-items:center;gap:10px;cursor:pointer;flex-shrink:0}
  .b-brand-name{font-family:'DM Mono',monospace;font-size:13px;font-weight:700;color:#2b8259;letter-spacing:0.2em}
  .b-back{background:none;border:1px solid rgba(45,122,98,0.2);color:#5a8a78;border-radius:7px;padding:5px 10px;font-size:11px;cursor:pointer;font-family:'DM Mono',monospace}
  .b-back:hover{background:#1c2b25;color:#d4f5e8;border-color:#1c2b25}
  .b-filters{display:flex;gap:8px;align-items:center;flex:1;flex-wrap:wrap}
  .b-input{background:#f7faf8;border:1px solid #d8ede6;color:#1c2b25;border-radius:8px;padding:8px 12px;font-size:13px;font-family:'Outfit',sans-serif;outline:none}
  .b-input:focus{border-color:#2d7a62}
  .b-input::placeholder{color:#9bbfb2}
  .b-loc{flex:1;min-width:200px;max-width:320px}
  .b-num{width:90px}
  .b-sel{cursor:pointer}
  .b-search{background:#2d7a62;color:#fff;border:none;border-radius:8px;padding:9px 16px;font-size:13px;font-weight:600;cursor:pointer;font-family:'Outfit',sans-serif}
  .b-search:hover{background:#1c5448}
  .b-source-pill{font-family:'DM Mono',monospace;font-size:10px;letter-spacing:0.08em;text-transform:uppercase;color:#5a8a78;margin-left:8px}
  .b-main{flex:1;display:flex;overflow:hidden;min-height:0}
  .b-list{width:42%;max-width:560px;overflow-y:auto;background:#f2f8f5;padding:14px}
  .b-map{flex:1;position:relative;background:#dbe9e2}
  .b-map-empty{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;color:#5a8a78;font-size:14px;text-align:center;padding:24px}
  .b-card{background:#fff;border:1.5px solid transparent;border-radius:14px;overflow:hidden;margin-bottom:12px;cursor:pointer;transition:transform 0.15s,box-shadow 0.15s,border-color 0.15s;box-shadow:0 1px 4px rgba(0,0,0,0.05)}
  .b-card:hover{transform:translateY(-2px);box-shadow:0 8px 24px rgba(0,0,0,0.1)}
  .b-card.selected{border-color:#2d7a62;box-shadow:0 8px 24px rgba(45,122,98,0.2)}
  .b-card-img{width:100%;height:180px;object-fit:cover;background:#dbe9e2;display:block}
  .b-card-img-placeholder{width:100%;height:180px;background:linear-gradient(135deg,#dbe9e2,#b2d4c0);display:flex;align-items:center;justify-content:center;color:#2d7a62;font-family:'Playfair Display',serif;font-size:32px}
  .b-card-body{padding:14px 16px}
  .b-card-price{font-family:'Playfair Display',serif;font-size:22px;font-weight:700;color:#1c2b25;letter-spacing:-0.02em;margin:0 0 4px}
  .b-card-stats{display:flex;gap:10px;font-size:12px;color:#5a8a78;font-family:'DM Mono',monospace;letter-spacing:0.04em;margin-bottom:6px}
  .b-card-stats strong{color:#1c2b25;font-weight:700}
  .b-card-addr{font-size:13px;color:#1c2b25;margin:0;line-height:1.4}
  .b-card-city{font-size:11px;color:#7aad98;margin-top:2px;letter-spacing:0.04em;text-transform:uppercase;font-family:'DM Mono',monospace}
  .b-fav{position:absolute;top:10px;right:10px;width:34px;height:34px;border-radius:50%;background:rgba(255,255,255,0.92);border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;font-size:16px;backdrop-filter:blur(4px);transition:transform 0.12s}
  .b-fav:hover{transform:scale(1.1)}
  .b-fav.saved{color:#c0354a}
  .b-card-img-wrap{position:relative}
  .b-loading{padding:40px;text-align:center;color:#5a8a78;font-style:italic}
  .b-empty{padding:40px;text-align:center;color:#5a8a78}
  .b-count{font-family:'DM Mono',monospace;font-size:11px;color:#5a8a78;letter-spacing:0.06em;text-transform:uppercase;margin-bottom:10px}
  .b-marker{width:auto;padding:5px 10px;background:#1c2b25;color:#d4f5e8;border-radius:14px;font-family:'DM Mono',monospace;font-size:12px;font-weight:700;cursor:pointer;border:2px solid #fff;box-shadow:0 2px 6px rgba(0,0,0,0.25);white-space:nowrap}
  .b-marker.selected{background:#2d7a62;transform:scale(1.1)}
  .b-detail{position:absolute;left:14px;right:14px;bottom:14px;background:#fff;border-radius:14px;padding:18px;box-shadow:0 12px 40px rgba(0,0,0,0.18);max-width:380px;z-index:5}
  .b-detail-close{position:absolute;top:10px;right:12px;background:none;border:none;font-size:18px;cursor:pointer;color:#5a8a78}
  .b-detail-price{font-family:'Playfair Display',serif;font-size:26px;font-weight:700;margin:0 0 6px}
  .b-detail-row{display:flex;gap:14px;font-size:12px;color:#5a8a78;font-family:'DM Mono',monospace;margin-bottom:8px}
  .b-detail-row strong{color:#1c2b25}
  .b-detail-addr{font-size:14px;color:#1c2b25;margin-bottom:10px}
  .b-detail-actions{display:flex;gap:8px}
  .b-detail-btn{flex:1;padding:9px 12px;border-radius:8px;border:none;cursor:pointer;font-size:12px;font-weight:600;font-family:'Outfit',sans-serif}
  .b-detail-btn.primary{background:#2d7a62;color:#fff}
  .b-detail-btn.primary:hover{background:#1c5448}
  .b-detail-btn.ghost{background:#f2f8f5;color:#1c2b25;border:1px solid #d8ede6}
  .b-detail-btn.ghost:hover{background:#e1eee6}
  .b-detail-btn.offer{background:#152d21;color:#7ecfb0}
  .b-detail-btn.offer:hover{background:#0a1810}
  .b-offer-overlay{position:fixed;inset:0;background:rgba(15,25,20,0.7);z-index:100;display:flex;align-items:flex-end;justify-content:center}
  @media(min-width:600px){.b-offer-overlay{align-items:center}}
  .b-offer-sheet{background:#fff;border-radius:20px 20px 0 0;width:100%;max-width:520px;max-height:92vh;overflow-y:auto;padding:24px 22px 32px}
  @media(min-width:600px){.b-offer-sheet{border-radius:20px;max-height:88vh}}
  .b-offer-head{display:flex;justify-content:space-between;align-items:flex-start;margin-bottom:18px}
  .b-offer-title{font-family:'Playfair Display',serif;font-size:22px;font-weight:700;color:#1c2b25;margin:0}
  .b-offer-addr{font-size:12px;color:#5a8a78;margin-top:4px;font-family:'DM Mono',monospace}
  .b-offer-close{background:none;border:none;font-size:22px;cursor:pointer;color:#5a8a78;padding:0;line-height:1}
  .b-offer-section{margin-bottom:20px}
  .b-offer-label{display:block;font-size:11px;font-weight:700;color:#5b7365;letter-spacing:0.08em;text-transform:uppercase;margin-bottom:6px}
  .b-offer-input{width:100%;background:#f7faf8;border:1.5px solid #d8ede6;color:#1c2b25;border-radius:10px;padding:11px 14px;font-size:15px;font-family:'Outfit',sans-serif;outline:none}
  .b-offer-input:focus{border-color:#2d7a62}
  .b-offer-price-wrap{position:relative}
  .b-offer-price-wrap .b-offer-input{padding-left:28px;font-size:20px;font-family:'Playfair Display',serif}
  .b-offer-dollar{position:absolute;left:12px;top:50%;transform:translateY(-50%);font-size:16px;color:#5a8a78;pointer-events:none}
  .b-offer-radios{display:flex;gap:8px;flex-wrap:wrap}
  .b-offer-radio{flex:1;min-width:90px;border:1.5px solid #d8ede6;border-radius:10px;padding:10px 12px;cursor:pointer;text-align:center;font-size:13px;color:#5a8a78;font-family:'DM Mono',monospace;transition:all .12s;background:#f7faf8}
  .b-offer-radio.selected{border-color:#2d7a62;background:#e4f5ee;color:#1c2b25;font-weight:600}
  .b-offer-checks{display:flex;flex-direction:column;gap:8px}
  .b-offer-check{display:flex;align-items:center;gap:10px;font-size:13px;color:#1c2b25;cursor:pointer}
  .b-offer-check input{width:16px;height:16px;accent-color:#2d7a62;cursor:pointer;flex-shrink:0}
  .b-offer-row{display:grid;grid-template-columns:1fr 1fr;gap:12px}
  .b-offer-help{font-size:11px;color:#9bbfb2;margin-top:4px;font-family:'DM Mono',monospace}
  .b-offer-submit{width:100%;background:#2d7a62;color:#fff;border:none;border-radius:12px;padding:14px;font-size:15px;font-weight:700;cursor:pointer;font-family:'Outfit',sans-serif;margin-top:8px}
  .b-offer-submit:hover:not(:disabled){background:#1c5448}
  .b-offer-submit:disabled{opacity:0.6;cursor:default}
  .b-offer-err{color:#c0354a;font-size:12px;margin-top:8px;font-family:'DM Mono',monospace}
  .b-offer-success{text-align:center;padding:24px 0}
  .b-offer-success-icon{font-size:48px;margin-bottom:12px}
  .b-offer-success h3{font-family:'Playfair Display',serif;font-size:22px;margin:0 0 8px;color:#1c2b25}
  .b-offer-success p{font-size:13px;color:#5a8a78;line-height:1.6;margin:0 0 16px}
  .b-offer-id{font-family:'DM Mono',monospace;font-size:11px;color:#9bbfb2;letter-spacing:0.08em}
  @media(max-width:880px){
    .b-list{width:100%;max-width:none;flex:1}
    .b-map{display:none}
  }
  /* In demo mode keep both columns visible regardless of viewport width */
  ${IS_DEMO ? ".b-list{width:40%!important;max-width:480px!important;flex:initial!important} .b-map{display:flex!important;flex:1!important}" : ""}
  @media(max-width:600px){
    .b-top{padding:8px 12px;gap:8px}
    .b-brand-name{font-size:12px;letter-spacing:0.16em}
    .b-back{padding:5px 9px;font-size:10px}
    .b-filters{width:100%;gap:6px;order:3}
    .b-loc{flex:1 1 100%;max-width:none;min-width:0;width:100%}
    .b-num{flex:1 1 0;width:auto;min-width:0}
    .b-sel{flex:1 1 0;min-width:0}
    .b-search{flex:1 1 100%;width:100%;padding:11px 16px;font-size:14px}
    .b-source-pill{flex:1 1 100%;text-align:center;margin-left:0}
    .b-input{padding:10px 12px;font-size:16px} /* 16px prevents iOS zoom on focus */
    .b-list{padding:10px}
    .b-card-img,.b-card-img-placeholder{height:160px}
    .b-card-body{padding:12px 14px}
    .b-card-price{font-size:20px}
    .b-detail{left:8px;right:8px;bottom:8px;padding:14px;max-width:none}
    .b-count{margin-bottom:8px}
  }
`;

// ─── tiny brand mark ─────────────────────────────────────────────────────────
const Logo = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 40 40" fill="none">
    <rect width="40" height="40" rx="10" fill="#1c2b25" />
    <path d="M20 8L7 18V33H16V25H24V33H33V18L20 8Z" fill="#7ecfb0" />
    <rect x="17" y="25" width="6" height="8" fill="#1c2b25" opacity="0.4" />
  </svg>
);

// ─── Offer modal ─────────────────────────────────────────────────────────────
const FINANCING_TYPES = [
  { id: "cash",         label: "Cash" },
  { id: "conventional", label: "Conventional" },
  { id: "fha",          label: "FHA" },
  { id: "va",           label: "VA" },
];

function PrequalUpload({ onVerified }) {
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setErr(""); setResult(null);
    try {
      const base64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = (ev) => res(ev.target.result.split(",")[1]);
        r.onerror = rej;
        r.readAsDataURL(file);
      });
      const resp = await fetch("/api/verify", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type: "preapproval", file_base64: base64, file_mime: file.type }),
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.error || "Verification failed");
      setResult(data);
      if (data.is_preapproval && !data.expired) onVerified(data);
    } catch (ex) { setErr(ex.message); }
    setUploading(false);
  };

  const fmtAmount = (n) => n ? `$${Math.round(n).toLocaleString()}` : null;

  return (
    <div style={{ border: "1.5px dashed #d8ede6", borderRadius: 10, padding: 16, background: "#f7faf8" }}>
      {!result ? (
        <>
          <label style={{ display: "block", cursor: "pointer" }}>
            <input type="file" accept="application/pdf,image/jpeg,image/png" onChange={handleFile} style={{ display: "none" }} disabled={uploading} />
            <div style={{ textAlign: "center", color: uploading ? "#2d7a62" : "#5b7365" }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{uploading ? "⏳" : "📄"}</div>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{uploading ? "Verifying…" : "Upload pre-approval letter"}</div>
              <div style={{ fontSize: 11, color: "#9bbfb2", marginTop: 3, fontFamily: "'DM Mono',monospace" }}>PDF or image · your letter stays private</div>
            </div>
          </label>
          {err && <div style={{ color: "#c0354a", fontSize: 12, marginTop: 8, textAlign: "center" }}>{err}</div>}
        </>
      ) : !result.is_preapproval ? (
        <div style={{ color: "#c0354a", fontSize: 13, textAlign: "center" }}>
          This doesn't appear to be a pre-approval letter. Please upload the correct document.
          <label style={{ display: "block", marginTop: 8, cursor: "pointer", color: "#5a8a78", fontSize: 12 }}>
            <input type="file" accept="application/pdf,image/jpeg,image/png" onChange={handleFile} style={{ display: "none" }} />
            Try another file
          </label>
        </div>
      ) : result.expired ? (
        <div style={{ color: "#c0354a", fontSize: 13, textAlign: "center" }}>
          This pre-approval expired on {result.expiry_date}. Please get a current letter from your lender.
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ fontSize: 22, color: "#2d7a62" }}>✓</div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1c2b25" }}>Pre-approved {fmtAmount(result.approved_amount)}</div>
            <div style={{ fontSize: 11, color: "#5a8a78", fontFamily: "'DM Mono',monospace" }}>
              {result.lender_name || "Lender"}
              {result.expiry_date ? ` · expires ${result.expiry_date}` : ""}
            </div>
          </div>
          <label style={{ cursor: "pointer", fontSize: 11, color: "#9bbfb2", fontFamily: "'DM Mono',monospace" }}>
            <input type="file" accept="application/pdf,image/jpeg,image/png" onChange={handleFile} style={{ display: "none" }} />
            change
          </label>
        </div>
      )}
    </div>
  );
}

const DEMO_PREQUAL = DEMO_AUTOOFFER ? {
  is_preapproval: true,
  approved_amount: 1300000,
  lender_name: "Chase Bank",
  expiry_date: "2026-08-15",
  expired: false,
} : null;

function OfferModal({ listing, buyerId, onClose }) {
  const [form, setForm] = useState(DEMO_AUTOOFFER ? {
    offer_price: "1180000",
    financing_type: "conventional",
    earnest_money: "25000",
    closing_date: "2026-07-15",
    contingency_inspection: true,
    contingency_financing: true,
    contingency_appraisal: false,
    buyer_name: "Jordan Chen",
    buyer_email: "jordan.chen@demo.6zero.ai",
    buyer_phone: "(415) 555-0192",
    buyer_note: "We're a family of four looking to make this our forever home. Pre-approved and ready to close.",
  } : {
    offer_price: listing.price ? String(listing.price) : "",
    financing_type: "conventional",
    earnest_money: "",
    closing_date: "",
    contingency_inspection: true,
    contingency_financing: true,
    contingency_appraisal: true,
    buyer_name: "",
    buyer_email: "",
    buyer_phone: "",
    buyer_note: "",
  });
  const [prequal, setPrequal] = useState(DEMO_PREQUAL);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [offerId, setOfferId] = useState(null);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    const price = Math.round(parseFloat(form.offer_price.replace(/,/g, "")) * 100);
    if (!price || price < 1) { setErr("Enter a valid offer price."); return; }
    if (form.financing_type !== "cash" && !prequal) { setErr("Please upload your pre-approval letter to continue."); return; }
    if (!form.buyer_name.trim()) { setErr("Your name is required."); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.buyer_email)) { setErr("Enter a valid email."); return; }
    setBusy(true);
    try {
      const res = await fetch("/api/offers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          action: "submit",
          property_address: listing.address,
          property_city: [listing.city, listing.state].filter(Boolean).join(", "),
          listing_price_cents: listing.price ? Math.round(listing.price * 100) : null,
          buyer_name: form.buyer_name,
          buyer_email: form.buyer_email,
          buyer_phone: form.buyer_phone,
          offer_price_cents: price,
          financing_type: form.financing_type,
          pre_approval_cents: prequal?.approved_amount ? Math.round(prequal.approved_amount * 100) : null,
          earnest_money_cents: form.earnest_money ? Math.round(parseFloat(form.earnest_money.replace(/,/g, "")) * 100) : null,
          closing_date: form.closing_date || null,
          contingency_inspection: form.contingency_inspection,
          contingency_financing: form.contingency_financing,
          contingency_appraisal: form.contingency_appraisal,
          buyer_note: form.buyer_note,
          prequal_verified: prequal ? true : false,
          prequal_amount_cents: prequal?.approved_amount ? Math.round(prequal.approved_amount * 100) : null,
          prequal_lender: prequal?.lender_name || null,
          prequal_loan_type: prequal?.loan_type || null,
          prequal_expires: prequal?.expiry_date || null,
          prequal_buyer_name: prequal?.buyer_name || null,
          buyer_id: buyerId,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Submit failed");
      setOfferId(data.offer_id);
    } catch (ex) { setErr(ex.message); }
    setBusy(false);
  };

  return (
    <div className="b-offer-overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="b-offer-sheet">
        {offerId ? (
          <div className="b-offer-success">
            <div className="b-offer-success-icon">✓</div>
            <h3>Offer submitted</h3>
            <p>The seller will review your offer and respond within 24–48 hours.<br />Keep an eye on <strong>{form.buyer_email}</strong> for updates.</p>
            <div className="b-offer-id">Offer ID: {offerId}</div>
            <button className="b-offer-submit" style={{ marginTop: 20 }} onClick={onClose}>Done</button>
          </div>
        ) : (
          <form onSubmit={submit}>
            <div className="b-offer-head">
              <div>
                <h2 className="b-offer-title">Make an offer</h2>
                <div className="b-offer-addr">{listing.address}{listing.city ? `, ${listing.city}` : ""}</div>
              </div>
              <button type="button" className="b-offer-close" onClick={onClose}>×</button>
            </div>

            <div className="b-offer-section">
              <label className="b-offer-label">Your offer price *</label>
              <div className="b-offer-price-wrap">
                <span className="b-offer-dollar">$</span>
                <input className="b-offer-input" value={form.offer_price}
                  onChange={(e) => set("offer_price", e.target.value)}
                  placeholder={listing.price ? listing.price.toLocaleString() : "500,000"}
                  inputMode="numeric" />
              </div>
              {listing.price && (
                <div className="b-offer-help">Asking price: ${listing.price.toLocaleString()}</div>
              )}
            </div>

            <div className="b-offer-section">
              <label className="b-offer-label">Financing</label>
              <div className="b-offer-radios">
                {FINANCING_TYPES.map((f) => (
                  <div key={f.id} className={`b-offer-radio${form.financing_type === f.id ? " selected" : ""}`}
                    onClick={() => set("financing_type", f.id)}>{f.label}</div>
                ))}
              </div>
            </div>

            {form.financing_type !== "cash" && (
              <div className="b-offer-section">
                <label className="b-offer-label">Pre-approval letter *</label>
                {DEMO_AUTOOFFER ? (
                  <div style={{ border: "1.5px dashed #d8ede6", borderRadius: 10, padding: 16, background: "#f7faf8", display: "flex", alignItems: "center", gap: 12 }}>
                    <div style={{ fontSize: 22, color: "#2d7a62" }}>✓</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#1c2b25" }}>Pre-approved $1,300,000</div>
                      <div style={{ fontSize: 11, color: "#5a8a78", fontFamily: "'DM Mono',monospace" }}>Chase Bank · expires 2026-08-15 · AI-verified</div>
                    </div>
                  </div>
                ) : (
                  <PrequalUpload onVerified={setPrequal} />
                )}
              </div>
            )}

            <div className="b-offer-row b-offer-section">
              <div>
                <label className="b-offer-label">Earnest money</label>
                <div className="b-offer-price-wrap">
                  <span className="b-offer-dollar">$</span>
                  <input className="b-offer-input" value={form.earnest_money}
                    onChange={(e) => set("earnest_money", e.target.value)}
                    placeholder="10,000" inputMode="numeric" />
                </div>
              </div>
              <div>
                <label className="b-offer-label">Target close date</label>
                <input type="date" className="b-offer-input" value={form.closing_date}
                  onChange={(e) => set("closing_date", e.target.value)} />
              </div>
            </div>

            <div className="b-offer-section">
              <label className="b-offer-label">Contingencies</label>
              <div className="b-offer-checks">
                {[
                  ["contingency_inspection", "Inspection contingency"],
                  ["contingency_financing",  "Financing contingency"],
                  ["contingency_appraisal",  "Appraisal contingency"],
                ].map(([key, label]) => (
                  <label key={key} className="b-offer-check">
                    <input type="checkbox" checked={form[key]} onChange={(e) => set(key, e.target.checked)} />
                    {label}
                  </label>
                ))}
              </div>
            </div>

            <div className="b-offer-section">
              <label className="b-offer-label">Your name *</label>
              <input className="b-offer-input" value={form.buyer_name}
                onChange={(e) => set("buyer_name", e.target.value)} placeholder="Jane Smith" />
            </div>

            <div className="b-offer-row b-offer-section">
              <div>
                <label className="b-offer-label">Email *</label>
                <input type="email" className="b-offer-input" value={form.buyer_email}
                  onChange={(e) => set("buyer_email", e.target.value)} placeholder="you@email.com" />
              </div>
              <div>
                <label className="b-offer-label">Phone</label>
                <input className="b-offer-input" value={form.buyer_phone}
                  onChange={(e) => set("buyer_phone", e.target.value)} placeholder="(415) 555-0100" />
              </div>
            </div>

            <div className="b-offer-section">
              <label className="b-offer-label">Personal note (optional)</label>
              <textarea className="b-offer-input" value={form.buyer_note}
                onChange={(e) => set("buyer_note", e.target.value)}
                style={{ minHeight: 72, resize: "vertical", lineHeight: 1.55 }}
                placeholder="Tell the seller a bit about yourself and why you love this home…"
                maxLength={1000} />
            </div>

            {err && <div className="b-offer-err">{err}</div>}
            <button type="submit" className="b-offer-submit" disabled={busy}>
              {busy ? "Submitting…" : "Submit offer →"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

// ─── main app ────────────────────────────────────────────────────────────────
export default function BuyerApp() {
  // Gate
  if (typeof window !== "undefined" && localStorage.getItem(BETA_KEY) !== BETA_SECRET) {
    window.location.href = "/public";
    return null;
  }

  const auth = useAuth();
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [navMenuOpen, setNavMenuOpen] = useState(false);
  const [brokerId, setBrokerId] = useState(null);
  const unreadCount = useUnreadCount(auth?.user?.id || null);

  // Buyer tiering: view + disclosures are always free. Scheduling, comps,
  // and Ask Cisero chat require accepting sixZero as buyer's agent first —
  // a one-time confirmation persisted on profiles.buyer_agent_accepted_at.
  const [agentAccepted, setAgentAccepted] = useState(false);
  const [gateRequest, setGateRequest] = useState(null); // { label, onAccepted }
  const [gateBusy, setGateBusy] = useState(false);
  useEffect(() => {
    setAgentAccepted(Boolean(auth?.profile?.buyer_agent_accepted_at));
  }, [auth?.profile?.buyer_agent_accepted_at]);
  const requestAgentGate = (label, onAccepted) => {
    if (agentAccepted) { onAccepted(); return; }
    setGateRequest({ label, onAccepted });
  };
  const acceptAgent = async () => {
    if (!supabase || !auth?.user?.id) return;
    setGateBusy(true);
    const { error } = await supabase.from("profiles")
      .update({ buyer_agent_accepted_at: new Date().toISOString() })
      .eq("id", auth.user.id);
    setGateBusy(false);
    if (error) return;
    setAgentAccepted(true);
    track("buyer_agent_accepted", {});
    const pending = gateRequest;
    setGateRequest(null);
    pending?.onAccepted?.();
  };

  // Resolve broker ID from broker_client_links so messaging works.
  useEffect(() => {
    if (!supabase || !auth?.user?.id) return;
    supabase
      .from("broker_client_links")
      .select("broker_id")
      .eq("client_id", auth.user.id)
      .limit(1)
      .single()
      .then(({ data }) => { if (data?.broker_id) setBrokerId(data.broker_id); });
  }, [auth?.user?.id]); // eslint-disable-line
  const [filters, setFilters] = useState({
    location: "San Francisco, CA",
    price_min: "",
    price_max: "",
    beds_min: "",
    baths_min: "",
    type: "",
  });
  const [listings, setListings] = useState([]);
  const [source, setSource] = useState(null);
  const [hotListings, setHotListings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [offerTarget, setOfferTarget] = useState(null);
  const [detailListing, setDetailListing] = useState(null);
  const [valuation, setValuation] = useState(null);
  const [valBusy, setValBusy] = useState(false);
  const [shareModal, setShareModal] = useState(false);
  const [shareLink, setShareLink] = useState(null);
  const [shareName, setShareName] = useState("");
  const [shareBusy, setShareBusy] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [saved, setSaved] = useState(() => {
    try { return new Set(JSON.parse(localStorage.getItem("sz_saved") || "[]")); }
    catch { return new Set(); }
  });

  const mapEl = useRef(null);
  const mapInstance = useRef(null);
  const markersRef = useRef([]);

  // Persist saved set locally (instant UI, works signed-out too)
  useEffect(() => {
    localStorage.setItem("sz_saved", JSON.stringify(Array.from(saved)));
  }, [saved]);

  // On sign-in, merge in any saves from other devices/sessions that the
  // local cache doesn't have yet — server is additive, never removes a
  // locally-saved id the server doesn't know about.
  const [savedSnapshots, setSavedSnapshots] = useState({}); // id -> full listing object
  const [showSaved, setShowSaved] = useState(false);
  const [showAppointments, setShowAppointments] = useState(false);
  const [appointments, setAppointments] = useState(null);

  useEffect(() => {
    if (!showAppointments || !supabase || !auth?.user?.id) return;
    setAppointments(null);
    supabase.from("showings").select("*").eq("buyer_id", auth.user.id).order("requested_at", { ascending: true })
      .then(({ data }) => setAppointments(data || []));
  }, [showAppointments, auth?.user?.id]); // eslint-disable-line

  useEffect(() => {
    if (!supabase || !auth?.user?.id) return;
    supabase
      .from("buyer_saved_listings")
      .select("listing_id, listing")
      .eq("user_id", auth.user.id)
      .then(({ data, error }) => {
        if (error || !data) return;
        setSaved((prev) => {
          const next = new Set(prev);
          data.forEach((row) => next.add(row.listing_id));
          return next;
        });
        setSavedSnapshots((prev) => {
          const next = { ...prev };
          data.forEach((row) => { if (row.listing) next[row.listing_id] = row.listing; });
          return next;
        });
      });
  }, [auth?.user?.id]); // eslint-disable-line

  // Saved listings for the dedicated "My saved homes" view — works even for
  // ids saved in a past search session that isn't in the current `listings`
  // array, using the Supabase snapshot captured at save time. Falls back to
  // the current search results for signed-out/local-only saves.
  const savedListingsForView = useMemo(() => {
    return Array.from(saved)
      .map((id) => savedSnapshots[id] || listings.find((l) => l.id === id))
      .filter(Boolean);
  }, [saved, savedSnapshots, listings]);

  // Load Mapbox + initialize map
  useEffect(() => {
    if (!MAPBOX_TOKEN) return;
    let cancelled = false;
    loadMapbox().then((mapboxgl) => {
      if (cancelled || !mapEl.current) return;
      mapboxgl.accessToken = MAPBOX_TOKEN;
      mapInstance.current = new mapboxgl.Map({
        container: mapEl.current,
        style: "mapbox://styles/mapbox/light-v11",
        center: [-122.4194, 37.7749],
        zoom: 10.5,
      });
      mapInstance.current.addControl(new mapboxgl.NavigationControl(), "top-right");
    }).catch(() => {});
    return () => {
      cancelled = true;
      mapInstance.current?.remove();
      mapInstance.current = null;
    };
  }, []);

  // Run search
  const runSearch = async () => {
    setLoading(true);
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v != null) params.set(k, v); });
    try {
      const res = await fetch(`/api/listings?${params}`, { headers: { "x-beta-token": BETA_SECRET } });
      const data = await res.json();
      setListings(data.rows || []);
      setSource(data.source || null);
      setHotListings(data.hot || []);
      setSelectedId(null);
      track("listings_search", { ...filters, count: data.rows?.length || 0, source: data.source });
    } catch (e) {
      setListings([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (IS_DEMO) { setListings(DEMO_LISTINGS); setSource("demo"); return; }
    runSearch(); // eslint-disable-next-line
  }, []);

  // Demo auto-select / auto-offer
  useEffect(() => {
    if (!IS_DEMO || !DEMO_LISTINGS.length) return;
    if (DEMO_AUTOSEL || DEMO_AUTOOFFER) {
      setSelectedId(DEMO_LISTINGS[0].id);
      if (DEMO_AUTOOFFER) setOfferTarget(DEMO_LISTINGS[0]);
    }
    // eslint-disable-next-line
  }, []);

  // Sync markers with listings
  useEffect(() => {
    const map = mapInstance.current;
    if (!map || !window.mapboxgl) return;
    if (!map.loaded()) {
      map.once("load", () => placeMarkers(map));
    } else {
      placeMarkers(map);
    }
    function placeMarkers(m) {
      markersRef.current.forEach((mk) => mk.remove());
      markersRef.current = [];
      if (!listings.length) return;
      const bounds = new window.mapboxgl.LngLatBounds();
      listings.forEach((l) => {
        if (l.lat == null || l.lng == null) return;
        const el = document.createElement("button");
        el.className = `b-marker${selectedId === l.id ? " selected" : ""}`;
        el.textContent = fmtPriceShort(l.price);
        el.onclick = (e) => { e.stopPropagation(); setSelectedId(l.id); };
        const marker = new window.mapboxgl.Marker({ element: el })
          .setLngLat([l.lng, l.lat])
          .addTo(m);
        markersRef.current.push(marker);
        bounds.extend([l.lng, l.lat]);
      });
      if (!bounds.isEmpty()) m.fitBounds(bounds, { padding: 60, maxZoom: 14, duration: 600 });
    }
  }, [listings, selectedId]);

  // Scroll selected card into view
  useEffect(() => {
    if (!selectedId) return;
    const node = document.getElementById(`card-${selectedId}`);
    if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [selectedId]);

  const toggleSaved = (l) => {
    let nowSaved = false;
    setSaved((prev) => {
      const next = new Set(prev);
      const isSaved = next.has(l.id);
      nowSaved = !isSaved;
      if (isSaved) next.delete(l.id);
      else next.add(l.id);
      track(isSaved ? "listing_unsaved" : "listing_saved", { id: l.id, address: l.address, price: l.price });
      return next;
    });
    if (supabase && auth?.user?.id) {
      if (nowSaved) {
        supabase.from("buyer_saved_listings").upsert(
          { user_id: auth.user.id, listing_id: l.id, listing: l },
          { onConflict: "user_id,listing_id" }
        ).then(() => {});
      } else {
        supabase.from("buyer_saved_listings")
          .delete().eq("user_id", auth.user.id).eq("listing_id", l.id)
          .then(() => {});
      }
    }
  };

  const selected = useMemo(() => listings.find((l) => l.id === selectedId) || null, [listings, selectedId]);

  const createShare = async () => {
    const savedListings = listings.filter((l) => saved.has(l.id));
    if (!savedListings.length) return;
    setShareBusy(true);
    try {
      const res = await fetch("/api/listings", {
        method: "POST",
        headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
        body: JSON.stringify({ action: "shortlist_create", name: shareName || "Our Home Search", listings: savedListings }),
      });
      const data = await res.json();
      if (data.ok) {
        const link = `${window.location.origin}/share/${data.id}?token=${data.viewer_token}`;
        setShareLink(link);
        track("shortlist_created", { count: savedListings.length });
      }
    } finally { setShareBusy(false); }
  };

  const copyShareLink = () => {
    if (!shareLink) return;
    navigator.clipboard.writeText(shareLink).then(() => {
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    });
  };

  // Fetch AI fair-price estimate when a listing is selected.
  useEffect(() => {
    if (!selected) { setValuation(null); return; }
    let cancelled = false;
    setValuation(null);
    setValBusy(true);
    const factsLine = [
      selected.beds != null && `${selected.beds} beds`,
      selected.baths != null && `${selected.baths} baths`,
      selected.sqft && `${selected.sqft.toLocaleString()} sqft`,
      selected.year_built && `built ${selected.year_built}`,
    ].filter(Boolean).join(" / ");
    const addr = [selected.address, selected.city, selected.state].filter(Boolean).join(", ");
    const normAddr = addr.toLowerCase().replace(/\s+/g, " ").replace(/[.,]/g, "").trim();
    fetch("/api/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
      body: JSON.stringify({
        model: "claude-haiku-4-5-20251001",
        max_tokens: 2000,
        system: "You are a real estate appraiser. Produce a concise fair-price estimate for a home buyer. Use web_search to find recent comparable sales. Numbers only — no disclaimers.",
        messages: [{ role: "user", content: `Property: ${addr}\n${factsLine}\n\nOutput EXACTLY:\n**Fair price range: $X,XXX,XXX – $X,XXX,XXX**\n**Recommended offer: $X,XXX,XXX**\nOne sentence: why.\nUnder 60 words total.` }],
        mode: "buyer_valuation",
        cache_key: `valuation:${normAddr}`,
        cache_type: "valuation",
      }),
    })
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        const text = d.content?.map((b) => b.text || "").join("") || "";
        const m = text.match(/\$\s*([\d.,]+)\s*([MmKk])?\s*[–\-—to]+\s*\$\s*([\d.,]+)\s*([MmKk])?/);
        if (!m) return;
        const parse = (s, suf) => { let n = parseFloat(s.replace(/,/g, "")); if (/m/i.test(suf || "")) n *= 1e6; if (/k/i.test(suf || "")) n *= 1e3; return n; };
        const low = parse(m[1], m[2]), high = parse(m[3], m[4]);
        if (low && high) setValuation({ low, high, midpoint: Math.round((low + high) / 2), cached: d.cached });
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setValBusy(false); });
    return () => { cancelled = true; };
  }, [selectedId]);

  return (
    <div className="b-app">
      <style>{CSS}</style>
      {offerTarget && <OfferModal listing={offerTarget} buyerId={auth?.user?.id || null} onClose={() => setOfferTarget(null)} />}
      {detailListing && (
        <PropertyDetail
          listing={detailListing}
          onClose={() => setDetailListing(null)}
          onMakeOffer={(l) => { setDetailListing(null); setOfferTarget(l); }}
          saved={saved.has(detailListing.id)}
          onToggleSave={toggleSaved}
          auth={auth}
          agentAccepted={agentAccepted}
          requestAgentGate={requestAgentGate}
        />
      )}
      {gateRequest && (
        <AgentGateModal
          label={gateRequest.label}
          busy={gateBusy}
          onAccept={acceptAgent}
          onClose={() => setGateRequest(null)}
        />
      )}
      {shareModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(28,43,37,0.6)", zIndex: 60, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: "100%", maxWidth: 420, boxShadow: "0 12px 40px rgba(0,0,0,0.18)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
              <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 18, fontWeight: 700 }}>Share with family</div>
              <button onClick={() => setShareModal(false)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: "#5a8a78" }}>×</button>
            </div>
            <div style={{ fontSize: 13, color: "#5a8a78", marginBottom: 16 }}>
              Share your {saved.size} saved home{saved.size !== 1 ? "s" : ""} with anyone — no account needed to view or react.
            </div>
            {!shareLink ? (
              <>
                <label style={{ fontSize: 12, color: "#5a8a78", display: "block", marginBottom: 6 }}>Name your shortlist</label>
                <input
                  style={{ width: "100%", background: "#f7faf8", border: "1px solid #d8ede6", color: "#1c2b25", borderRadius: 8, padding: "9px 12px", fontSize: 13, fontFamily: "inherit", outline: "none", marginBottom: 14, boxSizing: "border-box" }}
                  placeholder="Our SF Home Search"
                  value={shareName}
                  onChange={(e) => setShareName(e.target.value)}
                  maxLength={80}
                />
                <button
                  disabled={shareBusy}
                  onClick={createShare}
                  style={{ width: "100%", background: "#2d7a62", color: "#fff", border: "none", borderRadius: 10, padding: "11px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
                >
                  {shareBusy ? "Creating…" : "Create shareable link →"}
                </button>
              </>
            ) : (
              <>
                <div style={{ background: "#f2f8f5", border: "1px solid #c4e8d8", borderRadius: 10, padding: "10px 14px", marginBottom: 12, cursor: "pointer" }} onClick={copyShareLink}>
                  <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "#5a8a78", letterSpacing: "0.1em", marginBottom: 4 }}>SHAREABLE LINK</div>
                  <div style={{ fontSize: 12, color: "#1c2b25", wordBreak: "break-all" }}>{shareLink}</div>
                  <div style={{ fontSize: 11, fontFamily: "'DM Mono',monospace", color: "#2d7a62", marginTop: 4 }}>
                    {shareCopied ? "Copied!" : "Click to copy"}
                  </div>
                </div>
                <div style={{ fontSize: 12, color: "#9bbfb2", marginBottom: 14 }}>
                  Anyone with this link can view your saved homes and react — ❤️ Love it, 👍 Like, 🤔 Maybe, or ✗ Pass.
                </div>
                <button onClick={copyShareLink} style={{ width: "100%", background: "#1c5448", color: "#7ecfb0", border: "none", borderRadius: 10, padding: "11px", fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
                  {shareCopied ? "Copied!" : "Copy link"}
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {/* Messages slide panel */}
      {messagesOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 400, display: "flex" }} onClick={() => setMessagesOpen(false)}>
          <div style={{ flex: 1, background: "rgba(15,25,20,0.4)" }} />
          <div style={{ width: 360, background: "#fff", boxShadow: "-8px 0 40px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column", height: "100%" }} onClick={(e) => e.stopPropagation()}>
            <div style={{ padding: "14px 18px", borderBottom: "1px solid #f0f4f2", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
              <span style={{ fontFamily: "'DM Mono',monospace", fontSize: 12, fontWeight: 700, color: "#1c2b25", letterSpacing: "0.1em" }}>MESSAGES</span>
              <button onClick={() => setMessagesOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: "#9bbfb2", fontSize: 18, lineHeight: 1 }}>×</button>
            </div>
            <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column" }}>
              <MessageThread brokerId={brokerId} clientId={auth?.user?.id || null} myRole="buyer" clientName="Your broker" />
            </div>
          </div>
        </div>
      )}

      <header className="b-top">
        <div className="b-brand" onClick={() => (window.location.href = "/")}>
          <Logo />
          <div className="b-brand-name">6zero.ai</div>
        </div>
        <button
          className="b-back"
          onClick={() => { setShowSaved((v) => !v); setShowAppointments(false); }}
          style={{ marginLeft: 2, background: showSaved ? "#1c2b25" : undefined, color: showSaved ? "#d4f5e8" : undefined }}
        >{showSaved ? "← back to search" : `♥ Saved (${saved.size})`}</button>
        <div style={{ position: "relative", marginLeft: 2 }}>
          <button className="b-back" onClick={() => setNavMenuOpen((v) => !v)} title="More" style={{ position: "relative" }}>
            ☰
            {unreadCount > 0 && (
              <span style={{ position:"absolute", top:-4, right:-6, minWidth:16, height:16, background:"#c0354a", color:"#fff", borderRadius:8, fontSize:9, fontFamily:"'DM Mono',monospace", fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", padding:"0 4px", lineHeight:1 }}>
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>
          {navMenuOpen && (
            <>
              <div onClick={() => setNavMenuOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 99 }} />
              <div style={{ position: "absolute", top: "calc(100% + 6px)", right: 0, background: "#fff", border: "1px solid #d8ede6", borderRadius: 10, padding: 6, zIndex: 100, minWidth: 170, boxShadow: "0 8px 24px rgba(0,0,0,0.12)", display: "flex", flexDirection: "column", gap: 2 }}>
                <button className="b-back" style={{ textAlign: "left", border: "none" }} onClick={() => (window.location.href = "/")}>Home</button>
                <button className="b-back" style={{ textAlign: "left", border: "none" }} onClick={() => (window.location.href = "/sell/v2?view=dashboard")}>Sell a home</button>
                <button className="b-back" style={{ textAlign: "left", border: "none" }} onClick={() => { setShowAppointments((v) => !v); setShowSaved(false); setNavMenuOpen(false); }}>My appointments</button>
                <button className="b-back" style={{ textAlign: "left", border: "none" }} onClick={() => (window.location.href = "/marketplace")}>Services</button>
                <button className="b-back" style={{ textAlign: "left", border: "none" }} onClick={() => (window.location.href = "/account")}>Account</button>
                <button className="b-back" style={{ textAlign: "left", border: "none", position: "relative" }} onClick={() => { setMessagesOpen(true); setNavMenuOpen(false); }}>
                  Messages {unreadCount > 0 && `(${unreadCount > 9 ? "9+" : unreadCount})`}
                </button>
              </div>
            </>
          )}
        </div>
        <div className="b-filters">
          <AddressAutocomplete
            value={filters.location}
            onChange={(t) => setFilters({ ...filters, location: t })}
            onPick={(f) => {
              setFilters((p) => ({ ...p, location: f.place_name }));
              // Picking a result is an explicit search intent.
              setTimeout(runSearch, 0);
            }}
            onKeyDown={(e) => { if (e.key === "Enter") runSearch(); }}
            types="place,locality,neighborhood,postcode,district,region"
            placeholder="City, neighborhood, ZIP…"
            className="b-loc"
            inputClassName="b-input"
          />
          <input className="b-input b-num" type="number" placeholder="Min $"
            value={filters.price_min}
            onChange={(e) => setFilters({ ...filters, price_min: e.target.value })} />
          <input className="b-input b-num" type="number" placeholder="Max $"
            value={filters.price_max}
            onChange={(e) => setFilters({ ...filters, price_max: e.target.value })} />
          <select className="b-input b-sel" value={filters.beds_min}
            onChange={(e) => setFilters({ ...filters, beds_min: e.target.value })}>
            <option value="">Beds: any</option>
            <option value="1">1+</option><option value="2">2+</option><option value="3">3+</option>
            <option value="4">4+</option><option value="5">5+</option>
          </select>
          <select className="b-input b-sel" value={filters.baths_min}
            onChange={(e) => setFilters({ ...filters, baths_min: e.target.value })}>
            <option value="">Baths: any</option>
            <option value="1">1+</option><option value="2">2+</option><option value="3">3+</option>
          </select>
          <select className="b-input b-sel" value={filters.type}
            onChange={(e) => setFilters({ ...filters, type: e.target.value })}>
            <option value="">Type: any</option>
            <option value="single_family">House</option>
            <option value="condo">Condo</option>
          </select>
          <button className="b-search" onClick={runSearch} disabled={loading}>
            {loading ? "Searching…" : "Search"}
          </button>
          {source && <span className="b-source-pill">data: {source}</span>}
        </div>
      </header>

      <div className="b-main">
        {showAppointments ? (
          <div className="b-list" style={{ width: "100%", maxWidth: "none" }}>
            <div className="b-count">{appointments === null ? "Loading…" : `${appointments.length} appointment${appointments.length === 1 ? "" : "s"}`}</div>
            {appointments?.length === 0 && <div className="b-empty">No showings scheduled yet.</div>}
            {appointments?.map((a) => (
              <div key={a.id} className="b-card" style={{ cursor: "default", padding: 14 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: "#1c2b25", marginBottom: 4 }}>{a.listing?.address || "—"}</div>
                <div style={{ fontSize: 12, color: "#5a8a78", fontFamily: "'DM Mono',monospace" }}>
                  {new Date(a.requested_at).toLocaleString()} · <span style={{ textTransform: "capitalize" }}>{a.status}</span>
                </div>
              </div>
            ))}
          </div>
        ) : showSaved ? (
          <div className="b-list" style={{ width: "100%", maxWidth: "none" }}>
            <div className="b-count">{savedListingsForView.length} saved home{savedListingsForView.length === 1 ? "" : "s"}</div>
            {savedListingsForView.length === 0 ? (
              <div className="b-empty">Nothing saved yet — heart a listing to save it here.</div>
            ) : (
              savedListingsForView.map((l) => (
                <div key={l.id} className="b-card" onClick={() => setDetailListing(l)}>
                  <div className="b-card-img-wrap">
                    {l.photo
                      ? <img className="b-card-img" src={l.photo} alt={l.address} onError={(e) => { e.target.style.display = "none"; }} />
                      : <div className="b-card-img-placeholder">{l.beds || "?"}BR</div>}
                    <button
                      className={`b-fav${saved.has(l.id) ? " saved" : ""}`}
                      onClick={(e) => { e.stopPropagation(); toggleSaved(l); }}
                      title="Unsave"
                    >♥</button>
                  </div>
                  <div className="b-card-body">
                    {l.exclusive && (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#e4f7ef", color: "#2d7a62", border: "1px solid #b8e8d4", borderRadius: 8, padding: "2px 8px", fontSize: 10, fontFamily: "'DM Mono',monospace", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>
                        ★ sixZero exclusive
                      </div>
                    )}
                    <h3 className="b-card-price">{fmtPrice(l.price)}</h3>
                    <div className="b-card-stats">
                      <span><strong>{l.beds ?? "—"}</strong> bd</span>
                      <span><strong>{l.baths ?? "—"}</strong> ba</span>
                      <span><strong>{l.sqft ? l.sqft.toLocaleString() : "—"}</strong> sqft</span>
                      {l.year_built && <span>built {l.year_built}</span>}
                    </div>
                    <div className="b-card-addr">{l.address}</div>
                    <div className="b-card-city">{[l.city, l.state, l.zip].filter(Boolean).join(", ")}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        ) : (
        <>
        <div className="b-list">
          {hotListings.length > 0 && (
            <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: "1px solid #e1eee6" }}>
              <div style={{ fontFamily: "'DM Mono',monospace", fontSize: 11, letterSpacing: "0.08em", color: "#c47c1a", textTransform: "uppercase", marginBottom: 8 }}>
                🔥 Hot in this area
              </div>
              <div style={{ display: "flex", gap: 8, overflowX: "auto", paddingBottom: 4 }}>
                {hotListings.map((l) => (
                  <button
                    key={l.id}
                    onClick={() => setDetailListing(l)}
                    style={{ flexShrink: 0, width: 150, textAlign: "left", background: "#fff", border: "1.5px solid #f1d6a8", borderRadius: 10, padding: 8, cursor: "pointer" }}
                  >
                    {l.photo
                      ? <img src={l.photo} alt={l.address} style={{ width: "100%", height: 80, objectFit: "cover", borderRadius: 6, display: "block", marginBottom: 6 }} onError={(e) => { e.target.style.display = "none"; }} />
                      : <div style={{ width: "100%", height: 80, background: "#f2f8f5", borderRadius: 6, marginBottom: 6 }} />}
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#1c2b25" }}>{fmtPrice(l.price)}</div>
                    <div style={{ fontSize: 10, color: "#7aad98", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{l.address}</div>
                    {l.exclusive && <div style={{ fontSize: 9, color: "#2d7a62", fontFamily: "'DM Mono',monospace", marginTop: 2 }}>★ sixZero</div>}
                    {l.saved_count && <div style={{ fontSize: 9, color: "#c47c1a", fontFamily: "'DM Mono',monospace", marginTop: 2 }}>♥ {l.saved_count} buyers</div>}
                  </button>
                ))}
              </div>
            </div>
          )}
          {loading && listings.length === 0 ? (
            <div className="b-loading">Loading listings…</div>
          ) : listings.length === 0 ? (
            <div className="b-empty">No listings match. Try widening filters.</div>
          ) : (
            <>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <div className="b-count" style={{ margin: 0 }}>{listings.length} listing{listings.length === 1 ? "" : "s"}</div>
                {saved.size > 0 && (
                  <button
                    onClick={() => { setShareModal(true); setShareLink(null); setShareName(""); setShareCopied(false); }}
                    style={{ background: "#1c5448", color: "#7ecfb0", border: "none", borderRadius: 8, padding: "5px 12px", fontSize: 11, cursor: "pointer", fontFamily: "'DM Mono',monospace", letterSpacing: "0.06em" }}
                  >
                    ♥ {saved.size} saved · Share
                  </button>
                )}
              </div>
              {listings.map((l) => (
                <div
                  key={l.id}
                  id={`card-${l.id}`}
                  className={`b-card${selectedId === l.id ? " selected" : ""}`}
                  onClick={() => setSelectedId(l.id)}
                >
                  <div className="b-card-img-wrap">
                    {l.photo
                      ? <img className="b-card-img" src={l.photo} alt={l.address}
                          onError={(e) => { e.target.style.display = "none"; }} />
                      : <div className="b-card-img-placeholder">{l.beds || "?"}BR</div>}
                    <button
                      className={`b-fav${saved.has(l.id) ? " saved" : ""}`}
                      onClick={(e) => { e.stopPropagation(); toggleSaved(l); }}
                      title={saved.has(l.id) ? "Saved" : "Save"}
                    >{saved.has(l.id) ? "♥" : "♡"}</button>
                  </div>
                  <div className="b-card-body">
                    {l.exclusive && (
                      <div style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "#e4f7ef", color: "#2d7a62", border: "1px solid #b8e8d4", borderRadius: 8, padding: "2px 8px", fontSize: 10, fontFamily: "'DM Mono',monospace", letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: 6 }}>
                        ★ sixZero exclusive
                      </div>
                    )}
                    <h3 className="b-card-price">{fmtPrice(l.price)}</h3>
                    <div className="b-card-stats">
                      <span><strong>{l.beds ?? "—"}</strong> bd</span>
                      <span><strong>{l.baths ?? "—"}</strong> ba</span>
                      <span><strong>{l.sqft ? l.sqft.toLocaleString() : "—"}</strong> sqft</span>
                      {l.year_built && <span>built {l.year_built}</span>}
                    </div>
                    <div className="b-card-addr">{l.address}</div>
                    <div className="b-card-city">{[l.city, l.state, l.zip].filter(Boolean).join(", ")}</div>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="b-map" ref={mapEl}>
          {!MAPBOX_TOKEN && (
            <div className="b-map-empty">
              Map disabled — set <code>REACT_APP_MAPBOX_TOKEN</code> in your env to enable.
            </div>
          )}
          {selected && (
            <div className="b-detail">
              <button className="b-detail-close" onClick={() => setSelectedId(null)}>×</button>
              <div className="b-detail-price">{fmtPrice(selected.price)}</div>
              <div className="b-detail-row">
                <span><strong>{selected.beds ?? "—"}</strong> bd</span>
                <span><strong>{selected.baths ?? "—"}</strong> ba</span>
                <span><strong>{selected.sqft ? selected.sqft.toLocaleString() : "—"}</strong> sqft</span>
                {selected.year_built && <span>built {selected.year_built}</span>}
              </div>
              <div className="b-detail-addr">{selected.address}, {selected.city}, {selected.state}</div>

              {/* AI fair-price estimate */}
              {valBusy && !valuation && (
                <div style={{ fontSize: 11, color: "#9bbfb2", fontFamily: "'DM Mono',monospace", margin: "8px 0", letterSpacing: "0.06em" }}>
                  ★ estimating fair price…
                </div>
              )}
              {valuation && (
                <div style={{ background: "#f0faf5", border: "1px solid #c4e8d8", borderRadius: 8, padding: "8px 12px", margin: "8px 0" }}>
                  <div style={{ fontSize: 10, fontFamily: "'DM Mono',monospace", color: "#5a8a78", letterSpacing: "0.1em", marginBottom: 3 }}>
                    AI FAIR-PRICE ESTIMATE{valuation.cached ? " · cached" : ""}
                  </div>
                  <div style={{ fontFamily: "'Playfair Display',serif", fontSize: 15, fontWeight: 700, color: "#1c5448" }}>
                    {fmtPrice(valuation.low)} – {fmtPrice(valuation.high)}
                  </div>
                  {selected.price && (
                    <div style={{ fontSize: 10, color: "#5a8a78", marginTop: 2 }}>
                      Listed at {fmtPrice(selected.price)}{" "}
                      <span style={{ color: selected.price > valuation.high ? "#b05a2a" : selected.price < valuation.low ? "#2a7a52" : "#5a8a78" }}>
                        ({selected.price > valuation.high ? "above range" : selected.price < valuation.low ? "below range" : "within range"})
                      </span>
                    </div>
                  )}
                </div>
              )}

              <div className="b-detail-actions">
                <button className="b-detail-btn offer" onClick={() => (agentAccepted ? setOfferTarget(selected) : requestAgentGate("make an offer", () => setOfferTarget(selected)))}>{agentAccepted ? "Make an offer" : "🔒 Make an offer"}</button>
                <button className="b-detail-btn primary" onClick={() => setDetailListing(selected)}>Full details</button>
                <button className="b-detail-btn ghost" onClick={() => toggleSaved(selected)}>
                  {saved.has(selected.id) ? "♥" : "♡"}
                </button>
              </div>
            </div>
          )}
        </div>
        </>
        )}
      </div>
      <AskCisero auth={auth} savedCount={saved.size} agentAccepted={agentAccepted} requestAgentGate={requestAgentGate} />
    </div>
  );
}

// fire-and-forget tracking
function track(name, payload = {}) {
  fetch("/api/events", {
    method: "POST",
    headers: { "Content-Type": "application/json", "x-beta-token": BETA_SECRET },
    body: JSON.stringify({ name, payload }),
    keepalive: true,
  }).catch(() => {});
}
